import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  PIPELINE_BUCKET_STATUS_FILTER,
  PIPELINE_STATUS_TAB_BUCKETS,
  defaultPipelineStatusTab,
  sumPipelineTabCounts,
  type DashboardPipelineBucket,
  type PipelineStatusCounts,
  type PipelineStatusTab,
} from '@/lib/dashboardApplicationBuckets';

const bucketLabelKey: Record<DashboardPipelineBucket, string> = {
  APPLIED_NEW: 'dashboard.stateNew',
  READY_TO_PROCESS: 'application.readyToProcess.badge',
  PENDING_UPGRADE: 'application.pendingUpgrade.badge',
  PIPELINE_RUNNING: 'applications.processing',
  PIPELINE_STUCK: 'applications.processingStuck',
  AWAITING_DECISION: 'applications.awaitingDecision',
  PIPELINE_ERROR: 'applications.reviewRequired',
  HIRED: 'applications.hired',
  REJECTED: 'applications.rejected',
  WITHDRAWN: 'applications.withdrawn',
  OFFER_DECLINED: 'applications.offerDeclined',
};

const bucketTooltipKey: Record<DashboardPipelineBucket, string> = {
  APPLIED_NEW: 'dashboard.pipelineTooltip.new',
  READY_TO_PROCESS: 'dashboard.pipelineTooltip.ready',
  PENDING_UPGRADE: 'dashboard.pipelineTooltip.upgrade',
  PIPELINE_RUNNING: 'dashboard.pipelineTooltip.processing',
  PIPELINE_STUCK: 'dashboard.pipelineTooltip.stuck',
  AWAITING_DECISION: 'dashboard.pipelineTooltip.awaiting',
  PIPELINE_ERROR: 'dashboard.pipelineTooltip.error',
  HIRED: 'dashboard.pipelineTooltip.hired',
  REJECTED: 'dashboard.pipelineTooltip.rejected',
  WITHDRAWN: 'dashboard.pipelineTooltip.withdrawn',
  OFFER_DECLINED: 'dashboard.pipelineTooltip.offerDeclined',
};

const bucketAccent: Record<DashboardPipelineBucket, string> = {
  APPLIED_NEW: 'border-border/60',
  READY_TO_PROCESS: 'border-emerald-500/30',
  PENDING_UPGRADE: 'border-amber-500/40',
  PIPELINE_RUNNING: 'border-primary/30',
  PIPELINE_STUCK: 'border-orange-500/40',
  AWAITING_DECISION: 'border-warning/40',
  PIPELINE_ERROR: 'border-destructive/40',
  HIRED: 'border-success/30',
  REJECTED: 'border-border/60',
  WITHDRAWN: 'border-border/60',
  OFFER_DECLINED: 'border-warning/30',
};

const bucketValueTone: Record<DashboardPipelineBucket, string> = {
  APPLIED_NEW: 'text-foreground',
  READY_TO_PROCESS: 'text-emerald-700 dark:text-emerald-300',
  PENDING_UPGRADE: 'text-amber-700 dark:text-amber-300',
  PIPELINE_RUNNING: 'text-primary',
  PIPELINE_STUCK: 'text-orange-700 dark:text-orange-300',
  AWAITING_DECISION: 'text-warning',
  PIPELINE_ERROR: 'text-destructive',
  HIRED: 'text-success',
  REJECTED: 'text-muted-foreground',
  WITHDRAWN: 'text-muted-foreground',
  OFFER_DECLINED: 'text-warning',
};

const tabLabelKey: Record<PipelineStatusTab, string> = {
  action: 'dashboard.pipelineTab.action',
  pipeline: 'dashboard.pipelineTab.pipeline',
  outcomes: 'dashboard.pipelineTab.outcomes',
};

const tabHintKey: Record<PipelineStatusTab, string> = {
  action: 'dashboard.pipelineTab.actionHint',
  pipeline: 'dashboard.pipelineTab.pipelineHint',
  outcomes: 'dashboard.pipelineTab.outcomesHint',
};

/** Bucket count per tab — drives column layout so rows don't wrap awkwardly. */
const tabGridClass: Record<PipelineStatusTab, string> = {
  action: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
  pipeline: 'grid-cols-2 max-w-md',
  outcomes: 'grid-cols-2 sm:grid-cols-4',
};

interface PipelineStatusGridProps {
  counts: PipelineStatusCounts;
  className?: string;
}

function StatusCell({
  bucket,
  count,
  onNavigate,
}: {
  bucket: DashboardPipelineBucket;
  count: number;
  onNavigate: (status: string) => void;
}) {
  const { t } = useLanguage();
  const clickable = count > 0;
  const status = PIPELINE_BUCKET_STATUS_FILTER[bucket];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          disabled={!clickable}
          onClick={() => clickable && onNavigate(status)}
          className={cn(
            'min-w-0 w-full rounded-lg border bg-card px-2 py-2 sm:px-2.5 sm:py-2.5 text-left transition-colors',
            bucketAccent[bucket],
            clickable
              ? 'cursor-pointer hover:bg-muted/40 active:bg-muted/60'
              : 'cursor-default opacity-70',
          )}
        >
          <p className="text-[10px] sm:text-[11px] leading-tight text-muted-foreground line-clamp-2 min-h-[2.25em]">
            {t(bucketLabelKey[bucket])}
          </p>
          <p className={cn('mt-0.5 text-lg font-semibold tabular-nums', bucketValueTone[bucket])}>
            {count}
          </p>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[220px]">
        <p className="text-xs">{t(bucketTooltipKey[bucket])}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function TabBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-foreground">
      {count}
    </span>
  );
}

export function PipelineStatusGrid({ counts, className }: PipelineStatusGridProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const initialTab = useMemo(() => defaultPipelineStatusTab(counts), [counts]);
  const [activeTab, setActiveTab] = useState<PipelineStatusTab>(initialTab);

  const handleNavigate = (status: string) => {
    navigate(`/applications?status=${encodeURIComponent(status)}`);
  };

  const tabs: PipelineStatusTab[] = ['action', 'pipeline', 'outcomes'];

  return (
    <section
      aria-label={t('dashboard.pipelineStatus')}
      className={cn('space-y-3 min-w-0', className)}
    >
      <h2 className="text-sm font-semibold text-foreground">{t('dashboard.pipelineStatus')}</h2>
      <Card className="border-border/50 min-w-0">
        <CardContent className="pt-4">
          <p className="text-xs text-muted-foreground mb-3">{t('dashboard.pipelineStatusHint')}</p>
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as PipelineStatusTab)}
            className="min-w-0"
          >
            <TabsList className="grid w-full grid-cols-3 h-auto min-h-9 mb-3">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab}
                  value={tab}
                  className="text-xs sm:text-sm px-2 py-2 whitespace-normal leading-tight"
                >
                  <span className="inline-flex items-center justify-center gap-0.5">
                    {t(tabLabelKey[tab])}
                    <TabBadge count={sumPipelineTabCounts(counts, tab)} />
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>

            <TooltipProvider delayDuration={300}>
              {tabs.map((tab) => (
                <TabsContent key={tab} value={tab} className="mt-0 min-w-0">
                  <p className="text-xs text-muted-foreground mb-2">{t(tabHintKey[tab])}</p>
                  <div className={cn('grid gap-2 min-w-0', tabGridClass[tab])}>
                    {PIPELINE_STATUS_TAB_BUCKETS[tab].map((bucket) => (
                      <StatusCell
                        key={bucket}
                        bucket={bucket}
                        count={counts[bucket]}
                        onNavigate={handleNavigate}
                      />
                    ))}
                  </div>
                </TabsContent>
              ))}
            </TooltipProvider>
          </Tabs>
        </CardContent>
      </Card>
    </section>
  );
}

export default PipelineStatusGrid;
